"use client";

import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Radio,
  Tabs,
  Button,
  Tooltip,
} from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import advancedFormat from "dayjs/plugin/advancedFormat";

import {
  CloseOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  HomeOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import PropTypes from "prop-types";
import {
  addpatient,
  updatePatient,
} from "../../services/receptionist/patient/patient";
import { getNextOfficeId } from "../../services/receptionist/patient/patient"; // Ensure path is correct
import { useNavigate } from "react-router-dom";
import { Country, State } from "country-state-city";
import { useMessageApi } from "../../services/message api/MessageProvider";
import Loader from "../Loader";

// Extend dayjs with required plugins
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);

const { Option } = Select;

const AddPatientPopup = ({
  show,
  handleClose,
  editpatientsdetail,
  patientName = "",
  patientMobile = "",
}) => {
  const navigate = useNavigate();
  const messageApi = useMessageApi();
  const [form] = Form.useForm();

  const [country, setCountry] = useState();
  const [statevalue, setStateValue] = useState();

  const [activeTab, setActiveTab] = useState("personal");
  const [loading, setLoading] = useState(false);
  const [fetchingOfficeId, setFetchingOfficeId] = useState(false);

  // Initial State Setup
  const initialDateOfBirth = editpatientsdetail?.date_of_birth
    ? dayjs(editpatientsdetail.date_of_birth, "YYYY-MM-DD", true).isValid()
      ? dayjs(editpatientsdetail.date_of_birth, "YYYY-MM-DD")
      : null
    : null;

  const [formData, setFormData] = useState({
    first_name: patientName || editpatientsdetail?.first_name || "",
    middle_name: editpatientsdetail?.middle_name || "",
    last_name: editpatientsdetail?.last_name || "",
    date_of_birth: initialDateOfBirth,
    age: editpatientsdetail?.age || "",
    mobile: patientMobile || editpatientsdetail?.mobile || "",
    gender: editpatientsdetail?.gender || "",
    marital_status: editpatientsdetail?.marital_status || "",
    blood_group: editpatientsdetail?.blood_group || "",
    email: editpatientsdetail?.email || "",
    office_id: editpatientsdetail?.office_id || "",
    referred_by: "",
    group: "",
    preferred_language: "",
    flag: "",
    secondary_number: "",
    guardian: "",
    partner_name: "",
    partner_age: "",
    partner_profession: "",
    partner_blood_group: "",
    patient_profession: "",
    additional_notes: "",
    house_number: "",
    street: editpatientsdetail?.street || "",
    locality: "",
    pincode: editpatientsdetail?.pincode || "",
    status: editpatientsdetail?.status || "active",
    city: editpatientsdetail?.city || "",
  });

  // Fetch Next Office ID Function
  const fetchNextOfficeId = useCallback(async () => {
    try {
      setFetchingOfficeId(true);
      const res = await getNextOfficeId();
      if (res?.success && res?.data) {
        // Set state and update AntD form instance
        setFormData((prev) => ({ ...prev, office_id: res.data }));
        form.setFieldsValue({ office_id: res.data?.next_office_id });
      } else {
        messageApi.error(res?.error || "Failed to fetch Office ID");
      }
    } catch (error) {
      messageApi.error(error.message || "Error fetching Office ID");
    } finally {
      setFetchingOfficeId(false);
    }
  }, [form, messageApi]);

  // Load Country/State Options & Auto-fetch Office ID on Modal Open
  useEffect(() => {
    if (show) {
      const states = State.getStatesOfCountry("IN");
      const formattedStates = states.map((state) => ({
        label: state.name,
        value: state.name,
      }));
      const options = Country.getAllCountries()?.map((country) => ({
        label: country.name,
        value: country.name,
      }));
      setCountry(options);
      setStateValue(formattedStates);

      // Fetch Office ID if creating a NEW patient
      if (!editpatientsdetail) {
        fetchNextOfficeId();
      }
    }
  }, [show, editpatientsdetail, fetchNextOfficeId]);

  // Standard AntD form change handler
  const handleChange = (changedValues) => {
    setFormData((prev) => ({ ...prev, ...changedValues }));
  };

  const validateRequiredFields = () => {
    return form
      .validateFields()
      .then(() => true)
      .catch(() => false);
  };

  const handleSubmit = async () => {
    if (await validateRequiredFields()) {
      try {
        setLoading(true);
        const values = form.getFieldsValue();
        const payload = {
          ...formData,
          ...values,
          date_of_birth: form.getFieldValue("date_of_birth")?.format("YYYY-MM-DD") || null,
        };
        const result = await addpatient(payload);

        if (result?.success) {
          messageApi.success("Patient added successfully!");
          handleClose(result?.data);
          setLoading(false);
        } else {
          const errors = result?.error;
          if (errors && typeof errors === "object") {
            Object.entries(errors).forEach(([field, messages]) => {
              const messageArray = Array.isArray(messages) ? messages : [messages];
              messageArray.forEach((msg) => {
                messageApi.error({
                  content: `${field}: ${msg}`,
                  style: { maxWidth: "300px", minWidth: "300px", textAlign: "left", wordBreak: "break-word" },
                });
              });
            });
          }
          setLoading(false);
        }
      } catch (error) {
        messageApi.error("Failed to add patient");
        setLoading(false);
      }
    }
  };

  const handleUpdate = async () => {
    if (await validateRequiredFields()) {
      try {
        const values = form.getFieldsValue();
        values.date_of_birth = form.getFieldValue("date_of_birth")?.format("YYYY-MM-DD") || null;

        const result = await updatePatient(editpatientsdetail?.id, {
          ...formData,
          ...values,
        });

        if (result?.success) {
          messageApi.success("Patient updated successfully!");
          handleClose(result?.data);
        } else {
          const errors = result?.error;
          if (errors && typeof errors === "object") {
            Object.entries(errors).forEach(([field, messages]) => {
              const messageArray = Array.isArray(messages) ? messages : [messages];
              messageArray.forEach((msg) => {
                messageApi.error({
                  content: `${field}: ${msg}`,
                  style: { maxWidth: "300px", minWidth: "300px", textAlign: "left", wordBreak: "break-word" },
                });
              });
            });
          }
        }
      } catch (error) {
        messageApi.error("Failed to update patient");
      }
    }
  };

  // Parse initial DOB if string "YYYY-MM-DD"
  const parsedDOB = initialDateOfBirth
    ? dayjs(initialDateOfBirth, "YYYY-MM-DD")
    : null;

  // Parse age string safely
  let initialYears = "";
  let initialMonths = "0";

  if (editpatientsdetail?.age) {
    const ageStr = String(editpatientsdetail.age);
    const match = ageStr.match(/(\d+)Y\s*(\d+)M/i);

    if (match) {
      initialYears = match[1];
      initialMonths = match[2];
    } else if (!isNaN(Number(ageStr))) {
      initialYears = ageStr;
      initialMonths = "0";
    }
  }

  // Date inputs (dd/mm/yyyy)
  const [dd, setDd] = useState(parsedDOB ? parsedDOB.format("DD") : "");
  const [mm, setMm] = useState(parsedDOB ? parsedDOB.format("MM") : "");
  const [yyyy, setYyyy] = useState(parsedDOB ? parsedDOB.format("YYYY") : "");

  // Age inputs
  const [yrs, setYrs] = useState(initialYears);
  const [mo, setMo] = useState(initialMonths);

  const handleDateChange = (type, value) => {
    const newDd = type === "dd" ? value : dd;
    const newMm = type === "mm" ? value : mm;
    const newYyyy = type === "yyyy" ? value : yyyy;

    if (type === "dd") setDd(value);
    if (type === "mm") setMm(value);
    if (type === "yyyy") setYyyy(value);

    if (!newDd || !newMm || !newYyyy) {
      setYrs("");
      setMo("0");
      form.setFieldsValue({ date_of_birth: null, age: "" });
      return;
    }

    const birth = dayjs(`${newYyyy}-${newMm}-${newDd}`, "YYYY-MM-DD", true);
    if (birth.isValid()) {
      const now = dayjs();
      const ageYears = now.diff(birth, "year");
      const ageMonths = now.diff(birth.add(ageYears, "year"), "month");

      setYrs(ageYears.toString());
      setMo(ageMonths.toString());

      form.setFieldsValue({
        date_of_birth: birth,
        age: `${ageYears}Y ${ageMonths}M`,
      });
    } else {
      setYrs("");
      setMo("0");
      form.setFieldsValue({ date_of_birth: null, age: "" });
    }
  };

  const handleAgeChange = (type, value) => {
    const newYrs = type === "yrs" ? value : yrs;
    const newMo = type === "mo" ? value : mo;

    setYrs(newYrs);
    setMo(newMo);

    if (!newYrs || parseInt(newYrs) < 0) {
      form.setFieldsValue({ date_of_birth: null, age: "" });
      return;
    }

    form.setFieldsValue({
      age: `${newYrs}Y ${newMo || 0}M`,
    });
  };

  const renderDateAgeFields = (
    <div style={{ display: "flex", gap: "10px", width: "100%" }}>
      {/* Date of Birth Inputs */}
      <div style={{ display: "flex", flexDirection: "column", flex: 2 }}>
        <label style={{ fontSize: "12px", color: "rgba(0, 0, 0, 0.88)" }}>
          Date of Birth
        </label>
        <div style={{ display: "flex", gap: "5px" }}>
          <Input
            style={{ width: "25%" }}
            placeholder="dd*"
            value={dd}
            onChange={(e) => handleDateChange("dd", e.target.value)}
            maxLength={2}
          />
          <Input
            style={{ width: "25%" }}
            placeholder="mm*"
            value={mm}
            onChange={(e) => handleDateChange("mm", e.target.value)}
            maxLength={2}
          />
          <Input
            style={{ width: "50%" }}
            placeholder="yyyy*"
            value={yyyy}
            onChange={(e) => handleDateChange("yyyy", e.target.value)}
            maxLength={4}
          />
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", paddingTop: "20px" }}>
        or
      </div>

      {/* Age Inputs */}
      <div style={{ display: "flex", flexDirection: "column", flex: 2 }}>
        <label style={{ fontSize: "12px", color: "rgba(0, 0, 0, 0.88)" }}>
          Age
        </label>
        <div style={{ display: "flex", gap: "5px" }}>
          <Input
            style={{ width: "30%" }}
            placeholder="Yrs*"
            value={yrs}
            onChange={(e) => handleAgeChange("yrs", e.target.value)}
          />
          <Input
            style={{ width: "30%" }}
            placeholder="Mo"
            value={mo}
            onChange={(e) => handleAgeChange("mo", e.target.value)}
          />
        </div>
      </div>

      <Form.Item name="date_of_birth" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="age" hidden>
        <Input />
      </Form.Item>
    </div>
  );

  const tabItems = [
    {
      key: "personal",
      label: "Personal Info",
      children: (
        <div className="compact-form">
          <div className="form-row">
            <Form.Item
              name="first_name"
              label={
                <span>
                  First Name <span style={{ color: "red" }}>*</span>
                </span>
              }
              rules={[{ required: true, message: "First name is required" }]}
              className="form-col"
              normalize={(value) =>
                value
                  ? value
                      .toLowerCase()
                      .replace(/\b\w/g, (char) => char.toUpperCase())
                  : ""
              }
            >
              <Input prefix={<UserOutlined />} placeholder="First name" />
            </Form.Item>
            <Form.Item
              name="middle_name"
              label="Middle Name"
              className="form-col"
              normalize={(value) =>
                value
                  ? value
                      .toLowerCase()
                      .replace(/\b\w/g, (char) => char.toUpperCase())
                  : ""
              }
            >
              <Input placeholder="Middle name" />
            </Form.Item>
            <Form.Item
              name="last_name"
              label={
                <span>
                  Last Name <span style={{ color: "red" }}>*</span>
                </span>
              }
              className="form-col"
              rules={[{ required: true, message: "Last name is required" }]}
              normalize={(value) =>
                value
                  ? value
                      .toLowerCase()
                      .replace(/\b\w/g, (char) => char.toUpperCase())
                  : ""
              }
            >
              <Input placeholder="Last name" />
            </Form.Item>
          </div>

          <div className="form-row">
            <Form.Item
              name="mobile"
              label={
                <span>
                  Mobile Number <span style={{ color: "red" }}>*</span>
                </span>
              }
              rules={[{ required: true, message: "Mobile number is required" }]}
              className="form-col"
            >
              <Input prefix={<PhoneOutlined />} placeholder="Mobile number" />
            </Form.Item>
            <Form.Item name="email" label="Email Address" className="form-col">
              <Input prefix={<MailOutlined />} placeholder="Email" />
            </Form.Item>

            {/* Office ID Field with Refresh Button */}
            <Form.Item
              name="office_id"
              label="Office Id"
              className="form-col"
            >
              <Input
                placeholder="Office Id"
                suffix={
                  !editpatientsdetail && (
                    <Tooltip title="Refresh Office ID">
                      <Button
                        type="text"
                        size="small"
                        icon={<ReloadOutlined spin={fetchingOfficeId} />}
                        onClick={fetchNextOfficeId}
                        style={{ border: "none", background: "transparent" }}
                      />
                    </Tooltip>
                  )
                }
              />
            </Form.Item>
          </div>

          <div className="form-row">
            <div className="form-col" style={{ flex: 7 }}>
              {renderDateAgeFields}
            </div>

            <Form.Item
              name="gender"
              label={
                <span>
                  Gender <span style={{ color: "red" }}>*</span>
                </span>
              }
              rules={[{ required: true, message: "Gender is required" }]}
              className="form-col"
              style={{ flex: 3 }}
            >
              <Radio.Group>
                <Radio value="Male">Male</Radio>
                <Radio value="Female">Female</Radio>
              </Radio.Group>
            </Form.Item>
          </div>

          <div className="form-row">
            <Form.Item name="street" label="Address" className="form-col">
              <Input prefix={<HomeOutlined />} placeholder="A-123" />
            </Form.Item>
          </div>
          <div className="form-row">
            <Form.Item
              name="blood_group"
              label="Blood Group"
              className="form-col"
            >
              <Select placeholder="Blood group">
                <Option value="A+">A+</Option>
                <Option value="A-">A-</Option>
                <Option value="B+">B+</Option>
                <Option value="B-">B-</Option>
                <Option value="O+">O+</Option>
                <Option value="O-">O-</Option>
                <Option value="AB+">AB+</Option>
                <Option value="AB-">AB-</Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="marital_status"
              label="Marital Status"
              className="form-col"
            >
              <Select placeholder="Marital status">
                <Option value="single">Single</Option>
                <Option value="married">Married</Option>
                <Option value="divorced">Divorced</Option>
                <Option value="widowed">Widowed</Option>
              </Select>
            </Form.Item>
          </div>
          
        </div>
      ),
    },
    {
      key: "additional",
      label: "Additional",
      children: (
        <div className="compact-form">
          <div className="form-row">
            <Form.Item
              name="secondary_number"
              label="Secondary Number"
              className="form-col"
            >
              <Input prefix={<PhoneOutlined />} placeholder="10 digit number" />
            </Form.Item>

            <Form.Item name="guardian" label="Guardian" className="form-col">
              <Select placeholder="Guardian">
                <Option value="father">Father</Option>
                <Option value="mother">Mother</Option>
                <Option value="spouse">Spouse</Option>
                <Option value="other">Other</Option>
              </Select>
            </Form.Item>
          </div>

          <div className="form-row">
            <Form.Item
              name="partner_name"
              label="Partner's Name"
              className="form-col"
            >
              <Input placeholder="Partner name" />
            </Form.Item>

            <Form.Item name="age" label="Age" className="form-col">
              <Input placeholder="Age" disabled />
            </Form.Item>
          </div>

          <div className="form-row">
            <Form.Item
              name="partner_profession"
              label="Partner's Profession"
              className="form-col"
            >
              <Input placeholder="Profession" />
            </Form.Item>

            <Form.Item
              name="partner_blood_group"
              label="Partner's Blood Group"
              className="form-col"
            >
              <Select placeholder="Blood group">
                <Option value="A+">A+</Option>
                <Option value="A-">A-</Option>
                <Option value="B+">B+</Option>
                <Option value="B-">B-</Option>
                <Option value="O+">O+</Option>
                <Option value="O-">O-</Option>
                <Option value="AB+">AB+</Option>
                <Option value="AB-">AB-</Option>
              </Select>
            </Form.Item>
          </div>

          <div className="form-row">
            <Form.Item
              name="patient_profession"
              label="Patient's Profession"
              className="form-col"
            >
              <Input placeholder="Profession" />
            </Form.Item>

            <Form.Item name="status" label="Status" className="form-col">
              <Select placeholder="Status">
                <Option value="active">Active</Option>
                <Option value="inactive">Inactive</Option>
              </Select>
            </Form.Item>
          </div>
        </div>
      ),
    },
    {
      key: "address",
      label: "Address",
      children: (
        <div className="compact-form">
          <div className="form-row">
            <Form.Item name="street" label="Address" className="form-col">
              <Input prefix={<HomeOutlined />} placeholder="A-123" />
            </Form.Item>
          </div>

          <div className="form-row">
            <Form.Item name="pincode" label="Pincode" className="form-col">
              <Input placeholder="6 digit PIN" />
            </Form.Item>
            <Form.Item name="city" label="City" className="form-col">
              <Input placeholder="New Delhi" />
            </Form.Item>
          </div>

          <div className="form-row">
            <Form.Item name="state" label="State" className="form-col">
              <Select
                showSearch
                placeholder="Select State"
                options={statevalue}
                filterOption={(input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
            <Form.Item name="country" label="Country" className="form-col">
              <Select
                showSearch
                placeholder="Select Country"
                options={country}
                filterOption={(input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <Modal
          title={
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>{patientName ? patientName : "Patient"}&apos;s Info</span>
            </div>
          }
          open={show}
          onCancel={() => handleClose(null)}
          width={700}
          maskClosable={false}
          keyboard={false}
          footer={[
            <Button key="cancel" onClick={() => handleClose(null)}>
              Cancel
            </Button>,
            !editpatientsdetail ? (
              <Button key="submit" type="primary" onClick={handleSubmit}>
                Submit
              </Button>
            ) : (
              <Button key="update" type="primary" onClick={handleUpdate}>
                Update
              </Button>
            ),
          ]}
          closeIcon={<CloseOutlined />}
          getContainer={false}
          style={{ zIndex: 2000 }}
        >
          <Form
            form={form}
            layout="vertical"
            initialValues={formData}
            onValuesChange={handleChange}
            requiredMark={false}
          >
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={tabItems}
              className="patient-tabs"
              size="small"
            />
          </Form>
        </Modal>
      )}
    </>
  );
};

AddPatientPopup.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  patientName: PropTypes.string,
  patientMobile: PropTypes.string,
  editpatientsdetail: PropTypes.object,
};

export default AddPatientPopup; 